import { ComponentProps, PropsWithChildren, useEffect, useMemo, useRef } from 'react'

import { createMemoryRouter } from 'react-router-dom'

import { ThemeVariantProvider } from '@cash-design-system/react'
import { FlowProvider, FlowRenderer, useFlow } from '@cash-web/shared-blocker-feature-main'
import { FlowContextProps } from '@cash-web/shared-flow-data-access-context'
import { JourneyData } from '@cash-web/shared-journey-data-access-context'
import { useJourneyContext, useJourneyScope } from '@cash-web/shared-journey-data-access-context'

import { JourneyBaggageMiddleware } from './JourneyBaggageMiddleware'
import { JourneyOutletContext } from './JourneyOutletContext'
import { JourneyPaneRouter } from './JourneyPaneRouter'
import { JourneyRouter } from './JourneyRouter'
import { JourneyTargetRenderer } from './JourneyTargetRenderer'

type JourneyPaneCommonProps = {
  onCompleteFlow: () => void
  onCancelFlow: () => void
} & ComponentProps<typeof JourneyRouter>

type JourneyContentProps = JourneyPaneCommonProps & {
  isActive: boolean
  setActiveFlowRef: (flow: FlowContextProps) => void
  journeyData: JourneyData
}

type JourneyPaneProps = JourneyContentProps & {
  journeyData: JourneyData
}
export type JourneyPaneRendererProps = JourneyPaneCommonProps & {
  history: JourneyData[]
  onBack?: () => void
  onClose: () => void
}

const JourneyContent = ({
  onCompleteFlow,
  onCancelFlow,
  isActive,
  router,
  setActiveFlowRef,
  journeyData,
}: JourneyContentProps) => {
  const journey = useJourneyContext()
  const journeyScope = useJourneyScope()

  // isUnmounting is used to prevent calling onCompleteFlow if the journey is being unmounted
  const isUnmounting = useRef(false)
  // isFlowCompleted is used to prevent calling flow.complete if the flow has already been completed
  const isFlowCompleted = useRef(false)

  const flow = useFlow({
    onCompleteFlow: () => {
      /**
       * Skip journey.end() if component is unmounting (already removed from history)
       * This prevents navigation issues by ensuring we don't navigate back before journey has started
       */
      if (!isUnmounting.current) {
        isFlowCompleted.current = true
        journey.end(journeyData.journeyId)
      }
      // this is used to handle situations where the user can click a button
      onCompleteFlow()
    },
    // potentially in a form blocker that cancels the flow
    // and we want to navigate back to the previous journey
    onCancelFlow: () => {
      onCancelFlow()
    },
  })

  useEffect(() => {
    isUnmounting.current = false
    return () => {
      isUnmounting.current = true
      // if the journey is being unmounted, we need to complete the flow
      if (flow.isInProgress && !isFlowCompleted.current) {
        flow.complete()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.isInProgress])

  useEffect(() => {
    /**
     * Captures the active flow for the target
     */
    if (isActive) {
      setActiveFlowRef(flow)
    }
  }, [flow, isActive, setActiveFlowRef])

  if (!isActive) {
    return null
  }

  return (
    <ThemeVariantProvider themeVariant={journeyScope.themeVariant ?? 'default'}>
      <FlowProvider flow={flow}>
        {flow.isInProgress ? <FlowRenderer /> : <JourneyPaneRouter router={router} />}
      </FlowProvider>
    </ThemeVariantProvider>
  )
}

export const JourneyPane = ({ journeyData, router, ...props }: PropsWithChildren<JourneyPaneProps>) => {
  const paneRouter = useMemo(
    () =>
      createMemoryRouter(router.routes, {
        initialEntries: [
          {
            pathname: journeyData.path,
            state: { ...journeyData.opts?.state, scope: journeyData.opts?.scope },
            key: journeyData.journeyId,
          },
        ],
      }),
    [journeyData.journeyId, journeyData.path, journeyData.opts?.state, journeyData.opts?.scope, router.routes]
  )

  return (
    <JourneyBaggageMiddleware journey={journeyData} router={paneRouter}>
      <JourneyContent {...props} router={paneRouter} journeyData={journeyData} />
    </JourneyBaggageMiddleware>
  )
}

export const JourneyPaneRenderer = ({ children, ...props }: PropsWithChildren<JourneyPaneRendererProps>) => {
  return (
    <JourneyOutletContext.Provider value={props}>
      {children}
      <JourneyTargetRenderer target="modal" {...props} />
      <JourneyTargetRenderer target="fullscreen" {...props} />
      <JourneyTargetRenderer target="hidden" {...props} />
      <JourneyTargetRenderer target="moneybot" {...props} />
    </JourneyOutletContext.Provider>
  )
}
